import { useEffect, useRef, useState } from 'react';
import { Box, Link, TextField, Typography } from '@mui/material';
import { DialogProps } from '../../../interfaces/interfaces';
import { CustomerClient } from '../../../helpers/client';
import { Popup } from '../../popup/popup';
import { useTranslation } from 'react-i18next';
import { useReloginFlow } from '../../../hooks/useReloginFlow';
import { ReloginConfirmation } from '../login/reloginConfirmation';

export function ConfirmPhone(props: DialogProps) {
  const { onClose, open } = props;
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const customersClient: CustomerClient = new CustomerClient();
  const [inputValue, setInputValue] = useState('');
  const [success, setSuccess] = useState(false);
  const [failure, setFailure] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval>>(null);
  const { t } = useTranslation();
  const { showReloginDialog, reloginMessage, triggerRelogin, handleReloginConfirm } = useReloginFlow();

  useEffect(() => {
    return () => {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
      }
    };
  }, []);

  const startCooldown = () => {
    setResendCooldown(30);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    setFailure(false);
    setInputValue('');
    try {
      await customersClient.sendPhoneConfirmation();
      startCooldown();
    } catch {
      console.error('Failed to send confirmation sms');
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setFailure(false);
    onClose();
  };

  useEffect(() => {
    inputValue !== '' ? setIsButtonDisabled(false) : setIsButtonDisabled(true);
  }, [inputValue]);

  const confirmPhone = async () => {
    try {
      setFailure(false);
      await customersClient.confirmPhone(inputValue);
      setIsButtonDisabled(true);
      setSuccess(true);
      handleClose();
      triggerRelogin(t('account:phoneConfirmedSuccessfully'));
    } catch (error) {
      console.log(error);
      setFailure(true);
    }
  };

  return (
    <>
      <Popup
        submitOnEnter
        onClose={() => handleClose()}
        open={open}
        dialogTitle={t('account:confirmPhone')}
        dialogContent={
          <>
            <TextField
              autoFocus
              value={inputValue}
              fullWidth
              label={t('account:confirmationPhoneCode')}
              onChange={(e) => setInputValue(e.target.value)}
              variant="outlined"
              sx={{ mt: '10px' }}
              slotProps={{
                input: {
                  sx: {
                    borderRadius: '10px',
                  },
                },
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: '8px', gap: '2px' }}>
              <Typography sx={{ fontSize: '16px' }}>{t('account:didNotReceiveCode')}</Typography>
              <Link
                component="button"
                type="button"
                onClick={sendCode}
                sx={{
                  fontSize: '16px',
                  opacity: resendCooldown > 0 ? 0.5 : 1,
                  pointerEvents: resendCooldown > 0 ? 'none' : 'auto',
                }}
              >
                {resendCooldown > 0
                  ? `${t('account:resendConfirmationCode')} (${resendCooldown}s)`
                  : t('account:resendConfirmationCode')}
              </Link>
            </Box>
          </>
        }
        onAction={confirmPhone}
        buttonContent={t('account:confirm')}
        isButtonDisabled={isButtonDisabled}
        isSuccess={success}
        successText={t('account:successConfirmation')}
        isFailure={failure}
        failureText={t('account:confirmPhoneError')}
      />
      <ReloginConfirmation open={showReloginDialog} message={reloginMessage} onConfirm={handleReloginConfirm} />
    </>
  );
}
